/**
 * useOAuth Hook
 * React adapter for framework-agnostic OAuth Manager
 *
 * Usage:
 *   const { connections, connectPlatform, disconnect, isLoading } = useOAuth();
 */

import { useState, useEffect, useCallback } from 'react';
import { getOAuthManager } from '../lib/oauth';
import type { Platform, OAuthConnection, OAuthConnectionStatus } from '../lib/oauth';

export interface UseOAuthResult {
  // State
  connections: OAuthConnection[];
  isLoading: boolean;
  error: string | null;

  // Actions
  connectPlatform: (platform: Platform, returnUrl?: string) => Promise<void>;
  disconnect: (connectionId: string) => Promise<void>;
  refreshConnections: () => Promise<void>;
  getStatus: (platform: Platform) => Promise<OAuthConnectionStatus>;
  isConnected: (platform: Platform) => boolean;
  clearError: () => void;
}

export function useOAuth(): UseOAuthResult {
  const oauthManager = getOAuthManager();

  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to OAuth state changes
  useEffect(() => {
    const unsubscribe = oauthManager.subscribe((newConnections) => {
      setConnections(newConnections);
    });

    // Initial fetch
    oauthManager.getConnections().catch((err) => {
      console.error('Failed to fetch OAuth connections:', err);
    });

    return unsubscribe;
  }, []);

  // Sync loading state
  useEffect(() => {
    const interval = setInterval(() => {
      setIsLoading(oauthManager.isLoading());
      setError(oauthManager.getError());
    }, 100);

    return () => clearInterval(interval);
  }, []);

  /**
   * Connect a platform
   * Redirects to platform's OAuth authorization page
   */
  const connectPlatform = useCallback(
    async (platform: Platform, returnUrl?: string) => {
      try {
        await oauthManager.initiate(platform, returnUrl);
        // Note: This will redirect, so code after this won't execute
      } catch (err) {
        console.error('Failed to initiate OAuth:', err);
        throw err;
      }
    },
    []
  );

  /**
   * Disconnect a platform connection
   */
  const disconnect = useCallback(async (connectionId: string) => {
    try {
      await oauthManager.disconnect(connectionId);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      throw err;
    }
  }, []);

  /**
   * Refresh connections list
   */
  const refreshConnections = useCallback(async () => {
    try {
      await oauthManager.getConnections();
    } catch (err) {
      console.error('Failed to refresh connections:', err);
      throw err;
    }
  }, []);

  /**
   * Get connection status for a platform
   */
  const getStatus = useCallback(async (platform: Platform) => {
    return oauthManager.getStatus(platform);
  }, []);

  /**
   * Check if platform is connected
   */
  const isConnected = useCallback(
    (platform: Platform) => {
      return oauthManager.isConnected(platform);
    },
    [connections]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    oauthManager.clearError();
  }, []);

  return {
    connections,
    isLoading,
    error,
    connectPlatform,
    disconnect,
    refreshConnections,
    getStatus,
    isConnected,
    clearError,
  };
}
